import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CircularProgress, Grid, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Map, TileLayer, Polygon, WMSTileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import apiService from '../../services/api';
import PageTitle from '../../components/PageTitle';
// Create inline FarmSelector instead of importing the component

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

// Inline FarmSelector component to avoid import issues with Docker
function FarmSelector({ farms, currentFarmId, onSelectFarm, isLoading }) {
  const selectorStyles = makeStyles(theme => ({
    formControl: {
      minWidth: 300,
      marginBottom: theme.spacing(3)
    },
    select: {
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center'
      }
    },
    loadingSpinner: {
      marginLeft: theme.spacing(1),
      color: theme.palette.primary.main
    }
  }))();

  const handleChange = (event) => {
    const selectedFarm = farms.find(farm => farm.id === event.target.value);
    if (selectedFarm) {
      onSelectFarm(selectedFarm);
    }
  };

  return (
    <FormControl variant="outlined" className={selectorStyles.formControl}>
      <InputLabel id="farm-select-label">Select Farm</InputLabel>
      <Select
        labelId="farm-select-label"
        id="farm-select"
        value={currentFarmId || ''}
        onChange={handleChange}
        label="Select Farm"
        className={selectorStyles.select}
        disabled={isLoading}
        endAdornment={isLoading && <CircularProgress size={20} className={selectorStyles.loadingSpinner} />}
      >
        {farms.map((farm) => (
          <MenuItem key={farm.id} value={farm.id}>
            {farm.name} - {farm.crop_type || 'Unknown Crop'} ({farm.area_hectares ? `${farm.area_hectares.toFixed(2)} ha` : 'Unknown size'})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  mapCard: {
    marginBottom: theme.spacing(3),
    position: 'relative'
  },
  mapContainer: {
    height: 700,
    width: '100%',
    position: 'relative',
    zIndex: 0,
    '& .leaflet-container': {
      height: '100%',
      width: '100%'
    }
  },
  selectorContainer: {
    marginBottom: theme.spacing(3)
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1
  }
}));

// Helper function to calculate center of polygon coordinates
function calculateCenter(coordinates) {
  if (!coordinates || coordinates.length === 0) {
    // Default center if no coordinates (South Africa)
    return [-34.00256677544119, 19.491489831450753];
  }
  
  // Calculate average of all coordinate points
  let sumLat = 0;
  let sumLng = 0;
  
  // For GeoJSON format, the coordinates might be in [lng, lat] format
  // For standard Leaflet format, the coordinates are in [lat, lng] format
  // We'll handle both formats by determining which is which based on typical ranges
  const isLngLatFormat = Math.abs(coordinates[0][0]) > 90; // If > 90, it's likely longitude first
  
  for (let i = 0; i < coordinates.length; i++) {
    if (isLngLatFormat) {
      // Format is [lng, lat]
      sumLat += coordinates[i][1];
      sumLng += coordinates[i][0];
    } else {
      // Format is [lat, lng]
      sumLat += coordinates[i][0];
      sumLng += coordinates[i][1];
    }
  }
  
  return [
    sumLat / coordinates.length,
    sumLng / coordinates.length
  ];
}

// FarmMapDirect component
function FarmMapDirect() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [farms, setFarms] = useState([]);
  const [currentFarm, setCurrentFarm] = useState(null);
  const [mapCenter, setMapCenter] = useState([-29.0, 24.0]); // Default center of South Africa
  const [mapZoom, setMapZoom] = useState(5);
  const [farmPolygon, setFarmPolygon] = useState(null);

  // Fetch farms list on component mount
  useEffect(() => {
    async function fetchFarms() {
      try {
        setLoading(true);
        const data = await apiService.getFarms();
        if (data.farms && data.farms.length > 0) {
          setFarms(data.farms);
          // Select the first farm by default
          handleFarmSelect(data.farms[0]);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching farms:', err);
        setLoading(false);
      }
    }

    fetchFarms();
  }, []);

  // Handle farm selection
  const handleFarmSelect = async (farm) => {
    setCurrentFarm(farm);
    
    if (farm && farm.id) {
      try {
        setLoading(true);
        // Get detailed geometry for the farm
        const geometry = await apiService.getFarmGeometry(farm.id);
        setFarmPolygon(geometry);
        
        // Calculate center from the boundary
        if (farm.boundary && farm.boundary.coordinates && farm.boundary.coordinates[0]) {
          const coords = farm.boundary.coordinates[0];
          // Calculate center point (need to convert from [lng, lat] to [lat, lng])
          const lat = coords.reduce((sum, point) => sum + point[1], 0) / coords.length;
          const lng = coords.reduce((sum, point) => sum + point[0], 0) / coords.length;
          setMapCenter([lat, lng]);
          setMapZoom(15); // Zoom in when a farm is selected
        } else if (geometry.coordinates && geometry.coordinates.length > 0) {
          // Use geometry coordinates if available (already in [lat, lng] format)
          const latSum = geometry.coordinates.reduce((sum, point) => sum + point[0], 0);
          const lngSum = geometry.coordinates.reduce((sum, point) => sum + point[1], 0);
          const centerLat = latSum / geometry.coordinates.length;
          const centerLng = lngSum / geometry.coordinates.length;
          setMapCenter([centerLat, centerLng]);
          setMapZoom(15);
        }
      } catch (err) {
        console.error('Error fetching farm geometry:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Format polygon coordinates for react-leaflet
  // Leaflet expects coordinates in [lat, lng] format
  const getPolygonPositions = () => {
    if (farmPolygon && farmPolygon.coordinates && farmPolygon.coordinates.length > 0) {
      // Use the farm geometry coordinates directly (already in [lat, lng] format)
      return farmPolygon.coordinates;
    } else if (currentFarm && currentFarm.boundary && currentFarm.boundary.coordinates && currentFarm.boundary.coordinates[0]) {
      // Convert from [lng, lat] to [lat, lng] for Leaflet if using boundary from farm list
      return currentFarm.boundary.coordinates[0].map(point => [point[1], point[0]]);
    }
    return [];
  };

  return (
    <>
      <PageTitle title={`Farm Map: ${currentFarm ? currentFarm.name : 'Select a Farm'}`} />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Farm Selection" />
            <CardContent className={classes.selectorContainer}>
              <FarmSelector 
                farms={farms} 
                currentFarmId={currentFarm ? currentFarm.id : ''} 
                onSelectFarm={handleFarmSelect}
                isLoading={loading}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Farm Boundary with SANSA NDVI Layer" 
              subheader={currentFarm ? 
                `Farm: ${currentFarm.name} | Crop: ${currentFarm.crop_type || 'Unknown'} | Area: ${currentFarm.area_hectares ? currentFarm.area_hectares.toFixed(2) : '?'} ha` : 
                'Please select a farm to view'}
            />
            <div className={classes.mapContainer}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', backgroundColor: 'rgba(255,255,255,0.7)' }}>
                  <CircularProgress size={60} />
                </div>
              ) : (
                <Map
                  center={mapCenter}
                  zoom={mapZoom}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <WMSTileLayer
                    layers="cropwatch:ndvi_difference_map_20240520"
                    url="https://desa.sansa.org.za/ows"
                    transparent={true}
                    format="image/png"
                  />
                  {getPolygonPositions().length > 0 && (
                    <Polygon 
                      positions={getPolygonPositions()}
                      color="#4CAF50"
                      weight={3}
                      fillColor="#A5D6A7"
                      fillOpacity={0.4}
                    />
                  )}
                </Map>
              )}
            </div>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default FarmMapDirect;
